import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ExternalUserFile,
  SelectedFile,
} from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';
import { Button } from '@dynamika-shared/ui/Button/Button';
import {
  makeVisitFileListColumns,
  makeVisitFileListUploadedReportsColumns,
} from './VisitFileList.columns';
import { FileList } from './FileList';
import { UploadReportDrawer } from './UploadReportDrawer';
import { useExternalUserReportsSlice } from '@restore/ExternalUserReports/ExternalUserReports.slice';
import { twJoin } from '@dynamika-shared/utils/twMerge';

const MAX_DOWNLOAD_SIZE_MB = 1000;

interface VisitFileListProps {
  visitName: string;
  visitId: string;
  siteId: string;
  files: ExternalUserFile[];
  loading: boolean;
  onDownload: (fileId: string) => void;
  downloadingFileIds: Set<string>;
  // Visits without uploaded files are not created yet (e.g. an empty screening
  // visit), so uploading to them is not allowed.
  hasFiles: boolean;
}

export const VisitFileList: FC<VisitFileListProps> = ({
  visitName,
  visitId,
  siteId,
  files,
  loading,
  onDownload,
  downloadingFileIds,
  hasFiles,
}) => {
  const { patientId, clinicalTrialId } = useParams<{
    patientId: string;
    clinicalTrialId: string;
  }>();

  const [showModal, setShowModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);

  const onCheckFilesToDownload = useCallback((file: SelectedFile) => {
    setSelectedFiles(prev =>
      prev.some(f => f.id === file.id) ? prev.filter(f => f.id !== file.id) : [...prev, file]
    );
  }, []);

  const [
    uploadedReports,
    uploadedReportsLoading,
    fetchExternalUserReportList,
    downloadExternalUserReports,
  ] = useExternalUserReportsSlice((s, a) => [
    s.uploadedReports,
    s.loading,
    a.fetchExternalUserReportList,
    a.downloadExternalUserReports,
  ]);

  useEffect(() => {
    if (!clinicalTrialId || !patientId) return;
    fetchExternalUserReportList({ clinicalTrialId, patientId });
  }, [clinicalTrialId, patientId]);

  const fileListColumns = makeVisitFileListColumns(onDownload, downloadingFileIds);
  const uploadedReportsColumns = makeVisitFileListUploadedReportsColumns(
    onCheckFilesToDownload,
    selectedFiles
  );

  const selectedFilesSizeMB = useMemo(
    () => selectedFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024),
    [selectedFiles]
  );

  const uploadedReportsForVisit = useMemo(
    () => uploadedReports.filter(r => r.visitId === visitId),
    [uploadedReports, visitId]
  );

  if (!clinicalTrialId || !patientId) return null;

  const isOverSizeLimit = selectedFilesSizeMB > MAX_DOWNLOAD_SIZE_MB;
  const canUpload = hasFiles && !loading;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-2432 font-semibold">{visitName}</h2>

        <Button
          className={twJoin(
            'min-w-[144px]',
            canUpload &&
              'border-vermilion !bg-vividTangelo text-white hover:!border-vermilion'
          )}
          iconType="upload"
          iconClassName={twJoin(
            'w-[18px] h-[18px]',
            canUpload ? 'text-white' : 'text-silverSand'
          )}
          disabled={!canUpload}
          onClick={() => setShowModal(true)}
        >
          Upload
        </Button>
      </div>

      <FileList
        title="Files"
        columns={fileListColumns}
        data={files}
        loading={loading}
      />
      <div className="mb-4">
        <FileList
          title="Uploaded Reports"
          columns={uploadedReportsColumns}
          data={uploadedReportsForVisit}
          loading={uploadedReportsLoading}
          withAction={
            <div className="flex items-center justify-center">
              <div className="mr-3 flex">
                <span className="mr-1 text-1424 text-dimGray">Max. download limit:</span>
                <span
                  className={twJoin(
                    'text-1424 font-semibold text-chineseBlack',
                    isOverSizeLimit && 'text-vermilion',
                    selectedFilesSizeMB > 0 && 'text-dodgerBlue'
                  )}
                >
                  {selectedFilesSizeMB.toFixed(2)}
                </span>
                <div className="text-1424 font-semibold">
                  <span className="mx-0.5 font-medium">/</span>
                  <span className="text-chineseBlack">{MAX_DOWNLOAD_SIZE_MB}</span>
                  <span className="ml-0.5 text-dimGray">MB</span>
                </div>
              </div>
              <Button
                type="link"
                title="Download selected reports"
                className="without-bg !p-0 !text-[14px] !leading-6 !text-dodgerBlue hover:!text-dodgerBlue/80 disabled:opacity-30"
                iconType="download"
                iconClassName="w-[16px] h-[16px] !mr-0 text-dodgerBlue hover:text-dodgerBlue/80"
                disabled={selectedFiles.length === 0 || isOverSizeLimit}
                onClick={() => {
                  downloadExternalUserReports({
                    clinicalTrialId,
                    patientId,
                    visitName,
                    files: selectedFiles.map(f => ({ id: f.id, name: f.name })),
                  });
                  setSelectedFiles([]);
                }}
              >
                Download
              </Button>
            </div>
          }
        />
      </div>

      {!!showModal && (
        <UploadReportDrawer
          open={showModal}
          close={() => setShowModal(false)}
          visitId={visitId}
          siteId={siteId}
        />
      )}
    </div>
  );
};
